// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What each public page says, once, for the two things that say it (S2-02, S3-01).
//
// WHY ONE TABLE AND TWO CONSUMERS
// -------------------------------
// A page's title, description and opening sentence have to exist in two places that are not each
// other: the **prerendered HTML** a crawler receives, and the **React page** a visitor sees. Two
// copies of a sentence is how a page ends up claiming one thing to a search engine and another to
// a person, which is the definition of cloaking and is also just a bug. So the words live here and
// both consumers read them.
//
// **This file imports nothing**, for the same reason `sitemap.ts` imports nothing: `vite.config.ts`
// reaches it at build time and Vite's native config loader resolves a config's imports with Node's
// rules.
//
// WHAT IS HERE AND WHAT IS NOT
// ----------------------------
// * `sections` belong to a page **whose whole body is prose** — the six documents at the bottom of
//   this file, which `CopyPage` renders and the prerenderer writes. A crawler and a visitor get the
//   same words.
// * The application's own routes — the front door, the arcade, pricing, the Builder, a machine —
//   carry `title`, `description`, `h1` and `intro` and **no sections**. Their body is a machine, a
//   roster or an editor, and prose that only a crawler could see would be text written for robots.
//   The `intro` is a true sentence about what the route is, which is what the boot skeleton says
//   while React starts.
//
// LANGUAGE
// --------
// English only, and that is a recorded limitation rather than an oversight: the interface is fully
// bilingual (`i18n/fr.ts`) and these pages are not. Report 01 §C4 holds the decision — accept an
// English-only search presence, or serve `/fr/` — and it is deliberately taken after there is data
// rather than before.

/** A block of a prose page: a heading, and the paragraphs and bullets under it. */
export interface CopySection {
  readonly heading: string
  readonly paragraphs?: readonly string[]
  readonly bullets?: readonly string[]
  /** Verbatim blocks — headers, a curl, a config file — drawn in a `<pre>` (M12-18). */
  readonly code?: readonly string[]
  /**
   * One internal link this section ends with (M12-31).
   *
   * **Prose that names another page is not a link to it.** Three sections said "the Connect your
   * AI page steps through it" and a reader had to go and find it in the footer, which is also all
   * a crawler could do — and internal links with real anchor text are how whatever authority a
   * site earns reaches its pages (`test/seo/links.test.ts` is about exactly that failure on
   * `/pricing`). Both readers of this table draw it, so neither can have it and not the other.
   *
   * `to` is a route this site serves; `test/seo/links.test.ts` holds it to the sitemap.
   */
  readonly link?: { readonly to: string; readonly text: string }
}

/** Everything said about one page, in one place. */
export interface PageCopy {
  /** The whole `<title>`, brand included. Not composed from parts at the call site. */
  readonly title: string
  /** The `<meta name="description">`. Between 70 and 160 characters; a test holds it there. */
  readonly description: string
  /** The page's one `<h1>`. */
  readonly h1: string
  /** One true sentence about what this page is. Rendered, and written into the prerendered shell. */
  readonly intro: string
  /** The body, for a page whose body is prose. Absent for the application's own routes. */
  readonly sections?: readonly CopySection[]
  /**
   * Sections a route draws **underneath its own content** (S3-07).
   *
   * A different thing from `sections`, which means "this page's whole body is prose" and which
   * `bodyFor` reads to choose between the document layout and the boot skeleton. The arcade,
   * pricing and the Builder are a roster, a plan comparison and an editor; putting copy in
   * `sections` would have changed their layout as a side effect of adding words.
   *
   * **Every route with `below` has a component that renders it**, and
   * `test/seo/belowCopy.test.ts` asserts the pairing in both directions. Copy the prerenderer
   * writes and no page shows is the cloaking this module's reader forbids, and it is the silent
   * failure here: nothing else would have noticed.
   */
  readonly below?: readonly CopySection[]
  /**
   * Questions this page visibly answers (S6-04).
   *
   * Rendered by `CopyPage` **and** marked up as a `FAQPage` by the prerenderer, in that order of
   * importance: report 01 §C5 records the sibling product shipping an FAQ that existed only as
   * structured data, which is both a policy violation and a page nobody can read. Every question
   * here is on the page.
   */
  readonly faq?: readonly { readonly question: string; readonly answer: string }[]
  /**
   * The one thing this page asks its reader to do, drawn as the primary button under the lede
   * (S3-10).
   *
   * A machine guide exists to send somebody to the machine, and a link at the foot of a section is
   * not how a page says that. Both writers draw it — `CopyDocument` as a `Link`, the prerenderer
   * as an `<a>` with the same class — so a crawler and a reader meet the same button.
   */
  readonly action?: { readonly to: string; readonly text: string }
}

/** The site's own name, in one place, because it is in every title. */
export const SITE_NAME = 'HCC Pinball Arcade'

/**
 * The date the published policy pages last changed, as a reader sees it.
 *
 * **A privacy policy with no date is a privacy policy nobody can rely on** — it is the one thing a
 * reader needs to know whether what they agreed to has changed. Written here rather than generated
 * from a build date, because "the day the words changed" is not "the day the site was deployed",
 * and a date that moved on every deploy would say a policy changed when it had not.
 *
 * **Change this in the same commit as any change to the prose below.**
 */
export const POLICY_UPDATED = '3 September 2026'

/** Where somebody is sent to reach a person. Not an address: this product does not publish one. */
const CONTACT = 'https://www.holmesconsultants.com'

// -------------------------------------------------------------------------------------------
// The application's own routes
// -------------------------------------------------------------------------------------------

const APPLICATION: Readonly<Record<string, PageCopy>> = {
  '/': {
    // 58 characters, so it survives a search result's truncation with the proposition intact.
    title: 'HCC Pinball Arcade — pinball in your browser and on Quest 3',
    description:
      'Original pinball simulated at a thousand steps a second. Play NEON NOVA free in a browser or on a Quest 3 — no account, no download.',
    h1: 'Original pinball, in your browser',
    intro:
      'A machine is building here. Pull the plunger and play: there is no account to make and nothing to install.',
  },
  '/arcade': {
    title: `The machines — ${SITE_NAME}`,
    description:
      'Every machine this arcade can run, first-party and player-built. One is always free to play with no account; Pro unlocks the roster.',
    h1: 'The machines',
    intro:
      'Every machine this arcade can run — the ones we built, and the ones players published from the Machine Builder.',
      below: [
        {
          heading: 'What a machine is here',
          paragraphs: [
            'Every machine in this arcade is a definition rather than a program: a playfield, the devices on it, and the rules that score them. Nothing on the page is a file somebody uploaded — the art, the sounds and the lettering are all generated in code when the machine loads, which is why there is nothing to download and nothing to wait for.',
            'The same definition runs in a browser and on a Quest 3, against one simulation stepped a thousand times a second. A game you play in a headset can be re-simulated afterwards and produce the same score to the last digit.',
          ],
        },
        {
          heading: 'One is always free',
          paragraphs: [
            'NEON NOVA plays in full with no account, no download and no payment — the whole machine, the whole rule set, and the same physics every other machine uses. Pro unlocks the rest of the roster on every device you sign in on.',
          ],
        },
        {
          heading: 'Where the rest come from',
          paragraphs: [
            'We built the machines this arcade opened with, and players can publish more from the Machine Builder. A published machine is data in exactly the same way ours is — it cannot bring code with it, so playing a stranger\u2019s machine is as safe as playing one of ours.',
          ],
        },
      ],
  },
  '/play/:machineId': {
    // Replaced per machine by `machineCopy`. Present so the route has an entry when a machine is
    // not known yet — a cold navigation, an unknown id — rather than a page with no identity.
    title: `Play a machine — ${SITE_NAME}`,
    description:
      'Play a pinball machine in your browser, at a thousand simulation steps a second, with no account and nothing to install.',
    h1: 'A machine',
    intro: 'The machine this address names is being built. It plays in a browser and on a Quest 3.',
  },
  '/machines/:machineId': {
    // Replaced per machine by `machineAboutCopy` (S3-10), from the definition's own facts. Present
    // for the same reason the machine page's entry is: an address the router matches before it
    // knows which machine it is still needs an identity.
    title: `Machine guide — ${SITE_NAME}`,
    description:
      'The rules, the modes and the playfield of one machine in this arcade, written from its own definition, with a button to play it.',
    h1: 'A machine guide',
    intro: 'How one machine in this arcade plays: its rules, its modes, its playfield, and where to play it.',
  },
  '/pricing': {
    title: `Pricing — ${SITE_NAME}`,
    description:
      'What Pro costs and what it adds: the full roster, publishing, and building a machine with your own AI. One machine is always free, with no account.',
    h1: 'What it costs',
    intro:
      'One machine is free to play for ever, with no account. Pro unlocks the rest of the roster and the Machine Builder’s publishing.',
      below: [
        {
          heading: 'What free actually includes',
          paragraphs: [
            'One machine — NEON NOVA — with no account, no download and no time limit. Not a demo of a machine: the whole playfield, every mode, the full rule set, and the same thousand-steps-a-second simulation every other machine runs on. It plays in a browser and in a headset.',
          ],
        },
        {
          heading: 'What Pro adds',
          bullets: [
            'Building a machine with your own AI — Claude, Cursor, VS Code or OpenAI Codex — through tools that run on your own computer.',
            'The rest of the roster, on every device you sign in on.',
            'Publishing from the Machine Builder, so a machine you build can be played by other people.',
            'The machines players publish, as they arrive.',
          ],
          link: { to: '/connect', text: 'How building a machine with your own AI works' },
        },
        {
          heading: 'How the price is shown',
          paragraphs: [
            'Prices appear on this page and again at checkout in your own currency, because that is the only place they are true — they are set with the payment provider rather than written into the site. The annual plan is shown first at its full price, with what it works out to per month beside it, so the comparison is one you can check rather than one we assert.',
          ],
        },
      ],
    faq: [
      {
        question: 'Can my AI build a pinball machine for me?',
        answer:
          'Yes. You give the AI you already use a set of pinball tools that run on your own computer: it starts from a working template, checks its work against the same simulation the game runs, plays ten thousand headless balls to find anywhere the ball gets stuck, reads the rules back in plain English, and saves the finished machine to your account. Designing and test playing that way costs nothing; the key that lets the tools save to your account is part of Pro. The Connect your AI page steps through the setup.',
      },
      {
        question: 'Which AI assistants can do it?',
        answer:
          'Claude Code, Claude Desktop, Cursor, VS Code and OpenAI Codex — anything that can start a small program on your computer and talk to it. It needs Node.js 22 or newer, and setting it up is a ten-minute job you do once.',
      },
      {
        question: 'Can I do it from ChatGPT, or from Claude on my phone?',
        answer:
          'No, and there is no address to paste into them: those reach servers over the internet and never start a program on your machine. Building with an AI needs an assistant running on a computer you control. You can still build a machine by hand in the Machine Builder from any browser, on a phone included.',
      },
      {
        question: 'Do I have to pay for the AI as well?',
        answer:
          'Yes — the AI is your own, and whatever you already pay for it is what it costs to use. We charge nothing per machine and nothing per request. Pro is what lets the tools read and write your account.',
      },
    ],
  },
  '/builder': {
    title: `Machine Builder — ${SITE_NAME}`,
    description:
      'Design a pinball machine in your browser: playfield, devices, rules, art and sound. A machine is data, never code, and it plays everywhere.',
    h1: 'The Machine Builder',
    intro:
      'Lay out a playfield, wire its rules, hear it, and test-play it in the same window. A machine is data, never code.',
      below: [
        {
          heading: 'A machine is data, never code',
          paragraphs: [
            'What you build here is a definition — a layout, a set of devices, and rules over the events they raise. It is never a program. There is no scripting language to learn and no way to write one: rules run in a bounded evaluator with a fixed vocabulary of actions, and a published machine carries no code, no shaders and no uploaded files.',
            'That is what makes somebody else\u2019s machine safe to play, and it is why a machine built here runs unchanged in a browser and on a Quest 3.',
          ],
        },
        {
          heading: 'What you shape, in one window',
          bullets: [
            'The playfield: walls, ramps, targets, bumpers, slings and the flippers themselves.',
            'The rules: modes, scoring, and what each device does when the ball finds it.',
            'The sound and the display, generated the same way every other asset here is.',
            'A test game, on the machine you are editing, without leaving the page.',
          ],
        },
        {
          heading: 'Or let your AI build it',
          paragraphs: [
            'If you already use Claude, Cursor, VS Code or OpenAI Codex, you can point it at your account and let it do the building. It starts from a working template, validates against the same checks this page runs, plays ten thousand headless balls to find anywhere a ball gets stuck, reads its own rules back in plain English, and saves the result here for you to finish by hand.',
            'The tools run on your own computer — there is nothing to host and no address to paste anywhere — so it needs an assistant that can start a small program locally rather than one running in a browser or on a phone. The tools are free to download with any account, and the offline half of them needs no key at all; a key, which is what lets them save to your account, is part of Pro.',
          ],
          link: { to: '/connect', text: 'Set up your AI to build machines, step by step' },
        },
        {
          heading: 'Opening it costs nothing',
          paragraphs: [
            'The Builder opens for anyone, signed in or not, and creates nothing until you ask it to — it is a place to sit and try something. Publishing a machine so other people can play it is what Pro adds, and a published version goes through review before it appears on the arcade.',
          ],
        },
      ],
  },
}

/**
 * The copy for one machine's page, from the definition's own words.
 *
 * **This is the fix for two URLs that were one page.** Before it, `/play/neon-nova` and
 * `/play/iron-fathom` both titled themselves `Play`, rendered the same heading and shared the
 * site's description, so the sitemap offered two addresses that were indistinguishable to a
 * crawler. Both definitions already carried a title and a subtitle written for exactly this.
 *
 * The definitions are **read** here and never written — standing rule 1 of the SEO checklist.
 */
export function machineCopy(machine: {
  readonly title: string
  readonly subtitle?: string
}): PageCopy {
  const subtitle = machine.subtitle?.trim()
  const tail = subtitle === undefined || subtitle === '' ? '' : ` ${subtitle}`
  return {
    title: `${machine.title} — ${SITE_NAME}`,
    // Padded to clear the 70-character floor for a machine whose subtitle is short, without
    // inventing anything: every clause here is true of every machine this product runs.
    description:
      `${machine.title} — pinball, simulated at a thousand steps a second.${tail}` +
      ' Play it in a browser or on a Quest 3.',
    h1: machine.title,
    intro:
      subtitle === undefined || subtitle === ''
        ? `${machine.title}, in a browser or on a Quest 3.`
        : subtitle,
  }
}

// -------------------------------------------------------------------------------------------
// The prose pages
// -------------------------------------------------------------------------------------------

const PRIVACY: PageCopy = {
  title: `Privacy — ${SITE_NAME}`,
  description:
    'What this product keeps and what it never collects. Play anonymously and it learns nothing about you — no analytics SDK, no trackers, no profiling.',
  h1: 'Privacy',
  intro:
    'Play without an account and this product learns nothing about you. Sign in and it keeps the smallest set of things a leaderboard, a subscription and a friends list cannot work without.',
  sections: [
    {
      heading: 'The short version',
      paragraphs: [
        'Anonymous play creates no account, sends no request and writes no row anywhere. That is not a courtesy setting: it is a requirement of the product, and the build fails if an anonymous game ever reaches server code.',
        'There is no analytics library in the web page or in the Quest application. Not one.',
      ],
    },
    {
      heading: 'What is kept, and only while you have an account',
      bullets: [
        'Your email address — it is how you sign in, and it is held by our authentication provider rather than by this product.',
        'A handle, a display name, an avatar seed and a country — what other players see beside a score.',
        'Scores, and the input trace behind each one. A leaderboard score is re-simulated on the server to check it, and the trace is what gets re-simulated.',
        'Machines you build, and their versions. They are yours, and they are data rather than code.',
        'Paired headsets: the model, the account that paired it and when it was last used, so you can revoke one you no longer have.',
        'Subscription state: which tier, from where, and until when.',
      ],
    },
    {
      heading: 'Your avatar is a seed, not a picture',
      paragraphs: [
        'Every asset in this product is generated in code — zero image files, zero audio files, zero 3-D models — so there is nothing to upload, no upload to moderate, and no image of you anywhere.',
      ],
    },
    {
      heading: 'What is never collected',
      bullets: [
        'No analytics SDK, in the browser or in the headset application.',
        'No advertising, no behavioural profiling, and no data sold or shared with a broker.',
        'No third-party trackers. The front door makes no request to anywhere but this site, and a test fails the build if it ever does.',
        'No card details. Payment is handled by Stripe; card numbers never reach this product.',
        'No recordings. Voice in the arcade is peer-to-peer and live — not recorded, not stored, not transcribed.',
        'No hardware identifier. The installation id is minted rather than read off the device, so it means this copy on this headset and correlates with nothing else you own.',
      ],
    },
    {
      heading: 'Diagnostics, exhaustively',
      paragraphs: [
        'The whole diagnostic surface is a closed vocabulary of eighteen event kinds and eleven dimensions: renderer, device class, headset, operating system, browser, display rate, input method, machine id, machine version, route and reason.',
        'None of them is a person. Each exists because the product can act on it — a defect that only happens on one renderer, one device class, one refresh rate. Anything outside those lists is dropped on your device before it is sent, and the server drops it again on the way in.',
      ],
    },
    {
      heading: 'On a Quest',
      paragraphs: [
        'The headset application asks Meta for exactly three things: whether this account owns the application, an id scoped to us with your alias so the pairing screen can show which device is being linked, and a one-time proof that the id is really yours.',
        'The id is scoped to us. It is not your Meta account name and cannot be used to find you anywhere else. It is stored on the headset-link row and nowhere else.',
      ],
    },
    {
      heading: 'Your controls',
      bullets: [
        'Delete everything on this device — in Settings. Scores, replays and the unfinished game kept locally.',
        'Delete your account and everything attached to it. You have to type your handle back, because it is the one irreversible thing here.',
        'Revoke a paired headset, from the account page.',
        'Two separate mutes — "do not hear me" and "do not play them to me" — both remembered.',
      ],
      paragraphs: [
        'Two things account deletion deliberately does not do, and you are told rather than left to find out. It does not delete your sign-in identity, which is shared with other applications on the same account. And a subscription outlives the profile, because it lives with the payment provider — so cancel it there as well.',
      ],
    },
    {
      heading: 'Children',
      paragraphs: [
        'This product is rated 13+. It is not directed at children under 13 and does not knowingly keep data from them. Anonymous play needs no account and collects nothing at all, which is the mode a child can use without any of the above applying.',
      ],
    },
    {
      heading: 'Who publishes this, and how to reach us',
      paragraphs: [
        `HCC Pinball Arcade is published by Holmes Computer Consultants — ${CONTACT}. Questions about anything on this page go there, or through the support page.`,
      ],
    },
  ],
}

const ACCESSIBILITY: PageCopy = {
  title: `Accessibility — ${SITE_NAME}`,
  description:
    'What this product does for accessibility and what it does not do yet, audited in a real browser in both themes rather than asserted.',
  h1: 'Accessibility',
  intro:
    'This statement was written after an audit in a real browser in both themes, not instead of one. The audit found two defects and both are fixed.',
  sections: [
    {
      heading: 'Audited, not asserted',
      paragraphs: [
        'The audit measured this product against its own accessibility requirements in both the light and the dark theme. It found that nothing reached the 3:1 contrast a control needs for its boundary to be identifiable, and that every text input was unstyled. Both were invisible in the theme the developer had set, and both are fixed.',
      ],
    },
    {
      heading: 'What is built',
      bullets: [
        'Reduce flashing, as its own control, separate from reduce motion — the two come apart in both directions, and the system preference turns flash reduction on by default.',
        'Motion intensity as a separate setting, where the system’s reduced-motion preference wins.',
        'Seated play, offered when it is detected and never imposed.',
        'Machine placement measured from where you are, and a placement ritual that can be finished in a room nobody has scanned.',
        'A graphics tier that can be measured automatically or pinned by hand.',
        'A choice of nudge input — flick and device motion, or flick alone — which matters if you cannot move a phone.',
        'Light and dark appearance, fully implemented rather than a colour swap.',
        'Every input carries a title and every button carries a title and a type, as a project convention rather than a pass somebody did once.',
        'Two independent mutes, remembered across sessions.',
        'Spoken callouts are captioned on screen.',
      ],
    },
    {
      heading: 'What is not there, said plainly',
      bullets: [
        'No text scaling inside the machine’s display. It is a 5 × 7 dot-matrix panel, which is what a pinball display is; the interface around it does scale.',
        'No screen-reader narration of a game in progress. A ball in flight is a spatial, real-time thing and this product does not describe it.',
        'No one-handed control scheme. Both flippers, the plunger and the nudge assume two hands.',
        'No captions for the arcade’s live voice.',
      ],
      paragraphs: [
        'These are listed because a statement that names only what works is marketing. Each is a real gap and each is something somebody could need.',
      ],
    },
    {
      heading: 'Tell us what is missing',
      paragraphs: [
        `If something here stops you playing, that is a defect and we would like to know — ${CONTACT}, or through the support page.`,
      ],
    },
  ],
}

const TERMS: PageCopy = {
  title: `Terms — ${SITE_NAME}`,
  description:
    'The terms of using HCC Pinball Arcade: free play with no account, what a Pro subscription adds, how billing works, and who owns a machine you build.',
  h1: 'Terms',
  intro:
    'Plain terms for a product most people will use without ever making an account or paying anything.',
  sections: [
    {
      heading: 'Who you are dealing with',
      paragraphs: [
        `HCC Pinball Arcade is made and published by Holmes Computer Consultants — ${CONTACT}.`,
      ],
    },
    {
      heading: 'Playing without an account',
      paragraphs: [
        'You can play without an account, and doing so costs nothing and creates nothing. Scores and unfinished games are kept in your own browser, which means clearing your browser data clears them.',
      ],
    },
    {
      heading: 'What a subscription adds',
      paragraphs: [
        'A Pro subscription unlocks the machines beyond the free one, publishing from the Machine Builder, and the features the pricing page lists. Prices are shown on the pricing page and at checkout, in your own currency.',
      ],
    },
    {
      heading: 'Billing and cancellation',
      paragraphs: [
        'Payment is taken by Stripe. Every screen that changes money — subscribing, cancelling, swapping monthly for annual, updating a card, downloading an invoice — is hosted by Stripe rather than rebuilt here, and is reached from the account page.',
        'Cancelling stops the next renewal. Your Pro access continues until the date your account page shows, and no further payment is taken after you cancel.',
        'A subscription bought through a headset store is billed by that store and is cancelled there.',
      ],
    },
    {
      heading: 'Machines you build',
      paragraphs: [
        'A machine you build is yours. It is data rather than code, and it runs in a bounded evaluator with a fixed vocabulary of actions — which is what makes it safe to play somebody else’s machine.',
        'Publishing a machine makes it playable by other people. A published version is immutable: correcting it means publishing a new version, because a score is validated against the exact machine it was played on.',
        'A machine designed with an AI or through the Builder API is your machine, published under your name, subject to the same content rules and the same review, and you are responsible for it.',
      ],
    },
    {
      heading: 'What we ask of you',
      bullets: [
        'Do not attempt to submit a score you did not play. Every leaderboard score is re-simulated on the server, so a score that could not have happened is refused rather than ranked.',
        'Do not publish a machine that impersonates a real-world pinball machine, manufacturer or trade dress. Nothing in this product does, and nothing published through it may.',
      ],
    },
    {
      heading: 'Something wrong with a charge',
      paragraphs: [
        `Write to us — ${CONTACT}, or through the support page — and say what happened. An unexpected charge is a thing to fix rather than a thing to argue about.`,
      ],
    },
  ],
}

const SUPPORT: PageCopy = {
  title: `Support — ${SITE_NAME}`,
  description:
    'Answers to the five things that actually go wrong: an old version, a score that did not appear, a sign-out, a subscription not recognised, a sold headset.',
  h1: 'Support',
  intro:
    'The five questions this product is actually asked, and what each one really means. If none of them is yours, write to us.',
  sections: [
    {
      heading: '"It says my version is too old"',
      paragraphs: [
        'The leaderboard needs a build recent enough to be validated against. Solo play still works offline, and games you played meanwhile post themselves once you have updated — nothing is lost by waiting.',
      ],
    },
    {
      heading: '"My scores did not appear"',
      paragraphs: [
        'A score is queued, not lost. The key for a submission is derived from the game itself, so however long the wait, every game posts exactly once and nothing posts twice. A score that was held is released when the build number changes; a score that was refused is a different thing and says so.',
      ],
    },
    {
      heading: '"It signed me out"',
      paragraphs: [
        'A sign-out means the sign-in itself was refused, which is a real end to the session rather than a network problem. A failure, a rate limit or a hotel captive portal never signs anybody out.',
      ],
    },
    {
      heading: '"It says I am not subscribed, and I am"',
      paragraphs: [
        'Your tier is computed on the server and never guessed on the device, so the app can only ever under-claim it. Connect once and it will correct itself. If it does not, the account page shows what the server believes and that is the thing to tell us.',
      ],
    },
    {
      heading: '"I sold my headset"',
      paragraphs: [
        'Revoke the link from the account page. The link ends immediately; depending on how that headset signed in, its existing session may keep working until it expires, and the page tells you which case you are in rather than promising more than it can do.',
      ],
    },
    {
      heading: 'Anything else',
      paragraphs: [
        `Write to Holmes Computer Consultants — ${CONTACT}. Tell us what you did, what happened, and which device you were on; we will never ask you for a password or a token, and nothing we need would ever require one.`,
      ],
    },
  ],
}

const HOW_TO_PLAY: PageCopy = {
  title: `How to play — ${SITE_NAME}`,
  description:
    'Pinball controls on a keyboard, a phone and a Quest 3: the plunger is held rather than tapped, the flippers, the nudge and what a tilt costs.',
  h1: 'How to play',
  intro:
    'Pinball is two buttons, a plunger and a shove. Here is where each of them is on a keyboard, on a phone and in a headset.',
  sections: [
    {
      heading: 'The plunger is held, not tapped',
      paragraphs: [
        'How long you hold the plunger is how hard the ball is launched, and this is the one control that surprises people. A tap fires the weakest shot in the game — on many machines the lane awards are only collectable in a narrow band near the top of the range, so a player who taps every ball is locked out of a scoring feature without ever being told.',
        'On a keyboard, hold the space bar and let go. On a phone, drag the plunger down and release. In a headset, take hold of the rod and pull it back.',
      ],
    },
    {
      heading: 'The flippers',
      paragraphs: [
        'Left and right, and they are independent — a good player uses them separately far more than together. On a keyboard they are the left and right shift keys. On a phone, the lower left and lower right of the screen. In a headset, the buttons on the sides of the cabinet, pressed with your hands.',
      ],
    },
    {
      heading: 'The nudge, and the tilt',
      paragraphs: [
        'A shove moves the whole machine, which moves the ball. It is what saves a ball heading for an outlane. On a keyboard the arrow keys nudge; on a phone a flick does, and you can turn off the device-motion half if you cannot move the phone; in a headset you push the cabinet with an open hand.',
        'Nudge too much and the machine tilts, which ends the ball and takes its bonus. That is not a bug — it is the rule every real machine has, and learning where the line is is part of the game.',
      ],
    },
    {
      heading: 'A game is three balls, or five',
      paragraphs: [
        'How many balls a game gives you is the machine’s decision, and it is stated on the machine. When they are gone, the game is over and the score stands; if it is a good one, the machine asks for your initials.',
      ],
    },
    {
      heading: 'Playing in a headset',
      paragraphs: [
        'On a Quest 3 the machine is a full-size cabinet in your room, and you stand at it. The browser can enter VR directly; there is also a native application. Either way you can play seated, and the comfort settings are there to be used rather than to be found later.',
      ],
    },
  ],
  faq: [
    {
      question: 'Do I need an account to play?',
      answer:
        'No. Playing needs no account, no sign-in and no email address, and an anonymous game reaches no server at all. Your scores and your unfinished game are kept in your own browser.',
    },
    {
      question: 'Is there anything to download?',
      answer:
        'No. It runs in the browser on a desktop, a phone or a Quest 3. There is also a native Quest application for people who would rather launch it from the headset.',
    },
    {
      question: 'Why did my ball barely make it up the shooter lane?',
      answer:
        'Because the plunger is held rather than tapped: how long you hold it is how hard the ball goes. A tap fires the weakest shot in the game, which is also why the lane awards near the top of the range are the ones people never see.',
    },
    {
      question: 'What does tilt mean?',
      answer:
        'Nudging the machine too hard tilts it, which ends that ball and takes its bonus. Every real pinball machine does this, and learning where the line is is part of playing.',
    },
    {
      question: 'Can I play with somebody else?',
      answer:
        'Yes — up to four players take turns on one machine, and there is a virtual arcade where other people are in the room with you.',
    },
  ],
}

const ABOUT: PageCopy = {
  title: `About — ${SITE_NAME}`,
  description:
    'HCC Pinball Arcade is an original pinball product with one deterministic simulation shared by a browser and a headset, and every asset generated in code.',
  h1: 'About',
  intro:
    'An original pinball product that plays in a browser, in WebXR and as a native Quest application — one simulation behind all three.',
  sections: [
    {
      heading: 'One simulation, three places to play',
      paragraphs: [
        'The physics is not in the browser and it is not in the headset. It is a pure simulation, written twice — once in TypeScript and once in C# — and held identical by shared fixtures, so a game played on a headset can be re-simulated on a server and produce the same score to the last digit.',
        'That is what makes a leaderboard here worth something: a score is not a number the client sent, it is a recording of what you did, replayed.',
      ],
    },
    {
      heading: 'Every asset is generated in code',
      paragraphs: [
        'There is not one image file, audio file or 3-D model in this product. The playfield art, the sounds, the voice, the icons and the letterforms are all computed when they are needed.',
        'It began as a licensing decision and turned out to be a design one: it is why the machines can be built and shared without an upload to moderate, and why the whole front door is a few hundred kilobytes.',
      ],
    },
    {
      heading: 'Nothing you did not agree to',
      paragraphs: [
        'No analytics, no trackers, no advertising, and no account required to play. The privacy page says exactly what is kept and when.',
      ],
    },
    {
      heading: 'Who made it',
      paragraphs: [
        `Holmes Computer Consultants — ${CONTACT}.`,
      ],
    },
  ],
}

/**
 * Every page's copy, keyed by the route table's own path.
 *
 * A test holds these keys to the routes `INDEXING` marks indexable, so a page cannot be published
 * without somebody writing what it says, and copy cannot outlive the page it was written for.
 */

/**
 * `/developers` (M12-18): the Builder API and the MCP server, for a person with a terminal or an
 * AI. The words are `ai_docs/adding-api-mcp-functionality/developer-guide.md`, section for
 * section, and `test/docs/developer-guide.test.ts` holds the two together.
 */
const DEVELOPERS: PageCopy = {
  // Titled for what it documents rather than for who it is for (M12-31): nobody searches
  // "developers", and this is the page that should answer "HCC Pinball API" and "pinball MCP".
  title: `Builder API and MCP server — ${SITE_NAME}`,
  description:
    'The HTTP Builder API and the MCP server for HCC Pinball Arcade: scoped keys, every route they reach, the machine document, the budgets and the limits.',
  h1: 'Builder API and MCP server',
  intro:
    'A machine is a JSON document, never code — which is what makes it safe to let your own tools, or your own AI, design one.',
  sections: [
    {
      heading: 'What you can do',
      paragraphs: [
        'Any signed-in account can download the MCP server from the account page and run its local tools: start from a template, validate against the same validator the server runs, soak ten thousand balls, tune a shot, read the rulesheet, and write the machine to a file the browser Builder opens with “Open a file”.',
        'Saving a draft to your account, remixing, and publishing to the arcade go through the Builder API, which needs a key. Minting a key is part of Pro.',
      ],
    },
    {
      heading: 'Get a key',
      paragraphs: [
        'On the account page, under Builder API and AI tools, name the key and choose what it may do. A key is shown once, at mint; the list shows its label and its first eight characters. An account may hold 5 live keys, a key expires a year after it is made, and any tool using a revoked key stops at once.',
      ],
      bullets: [
        'machines:read — list your machines and read any version of your own, published or draft.',
        'machines:write — create machines, save drafts, remix a published machine, run the dry-run validator.',
        'machines:publish — publish a saved version to the arcade; the irreversible one, so it is its own scope.',
      ],
    },
    {
      heading: 'Every request',
      paragraphs: [
        'The base URL is https://rteunfaeoysdeqlbahsv.supabase.co/functions/v1/pinball-client-v1. Every request carries the public anon key, your key as the bearer, an identification block, and — on every POST — an idempotency key that stays the same across retries.',
        'Every answer is an envelope with a contractVersion, a requestId, a serverTime and either data or an error with a code, a message and, for a refused input, the fields it refused. Quote the requestId of any refusal when you write to support.',
      ],
      code: [
        'apikey: eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ0ZXVuZmFlb3lzZGVxbGJhaHN2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwMzE5MTMsImV4cCI6MjA4OTYwNzkxM30.aGLvfg3z6MO1xiKrlECKnesQvU7dJvKA4VO9cJTQq8w\nauthorization: Bearer hpk_…\nx-pinball-meta: {"clientKind":"tool","appVersion":"1.0.0","buildNumber":1,"contractMajor":1,"contractMinor":1,"requestId":"<uuid v4>"}\nidempotency-key: <8 to 128 characters of [A-Za-z0-9_-], the same across retries; every POST>',
      ],
      bullets: [
        'unauthenticated (401): no key, a revoked or expired key, or a key on a route that takes none.',
        'forbidden (403): a live key without the scope this route needs.',
        'entitlement_required (402): the account is not Pro; the message says which capability.',
        'moderation_blocked (451): a moderator has stopped this author; contact support.',
        'validation_failed (422): the document was refused; fields is keyed by document path.',
        'rate_limited (429): a cap below; fields names which.',
        'payload_too_large (413): over 524288 bytes.',
        'conflict (409): a version that already exists, a slug already taken, a remix in flight.',
      ],
    },
    {
      heading: 'The routes',
      bullets: [
        'GET /machines/mine — machines:read — your machines, with their versions and statuses.',
        'GET /machines/{machineId}/versions/{semver} — machines:read — one version; a key reads its owner’s drafts, and published versions are public.',
        'POST /machines — machines:write — a new machine from a title and an era; answers its id and the first version number.',
        'POST /machines/validate — machines:write — the verdict on a definition, the budgets and the moderation screen, without writing anything.',
        'POST /machines/{machineId}/versions — machines:write — save a draft under a semver; the document is revalidated here.',
        'POST /machines/{machineId}/versions/{semver}/publish — machines:publish — publish a saved version; a first publication goes to a person.',
        'POST /machines/{machineId}/versions/{semver}/remix — machines:write — fork a published version into a machine of your own, ancestry recorded.',
        'POST /api-keys, GET /api-keys, POST /api-keys/{keyId}/revoke, GET /package — a signed-in session only, never a key.',
      ],
      code: [
        'curl -s -X POST "$BASE/machines/validate" \\\n  -H "apikey: $ANON" -H "authorization: Bearer $HCC_PINBALL_API_KEY" \\\n  -H "x-pinball-meta: {\\"clientKind\\":\\"tool\\",\\"appVersion\\":\\"1.0.0\\",\\"buildNumber\\":1,\\"contractMajor\\":1,\\"contractMinor\\":1,\\"requestId\\":\\"$(uuidgen)\\"}" \\\n  -H "idempotency-key: validate-$(date +%s)" \\\n  -H "content-type: application/json" \\\n  --data-binary @machine.json',
      ],
      paragraphs: [
        'The full OpenAPI description is at https://pinballgame.ca/contracts/pinball/v1/openapi.yaml.',
      ],
    },
    {
      heading: 'The machine document',
      paragraphs: [
        'The schema is https://pinballgame.ca/contracts/pinball/v1/machine/schema.json — JSON Schema 2020-12, additionalProperties false everywhere, so an unknown field is refused by name. The format version is 2.0. NEON NOVA and IRON FATHOM are worked examples of every block, readable at their public version routes.',
        'The rules are a closed grammar: 18 actions and 23 operators, an expression depth of 16 and 256 nodes, and event chains to depth 8. There is nothing else to call.',
        'The validator sorts what it finds into seventeen classes, and every one runs on the server again at save and at publish: Schema; Bounds; Geometry — containment, overlap, flipper crossing, flipper outboard gap, trap and reachability; Layer integrity; References; Rules — termination, budget and reachability; Display; Art; Audio; Budgets; and Playability, which is the ten-thousand-ball soak the MCP server runs locally.',
      ],
      bullets: [
        'Colliders 400 · Devices 120 · Lamps 160 · Rules 400 · Modes 24 · Simultaneous balls 6.',
        'Art program ops per surface 250 · Display scenes 64 · Audio cues 120 · Document 524288 bytes.',
      ],
    },
    {
      heading: 'Limits',
      bullets: [
        '5 live keys per account.',
        '50 machines per author; 200 draft saves per author per day.',
        '5 publications per author per day; 50 published versions per author.',
        '120 saves or dry runs per key per hour.',
        'Validation takes about 20 ms per document, on the shipped machines and on the worst case at every budget.',
      ],
    },
    {
      heading: 'Content rules',
      paragraphs: [
        'No licensed or recognisable real-world machines, themes, manufacturers or trade dress. Ever. A machine that recreates a released table, borrows its name, its art or its rules, is refused by the product’s own screen and by a person.',
        'Every author’s first publication is reviewed by a person before it appears in the arcade; later publications are reviewed on report or on a sample. Text on the playfield, the display and in speech is screened for invisible characters, mixed scripts, contact details and trade dress, and the dry run returns that screen’s findings so you can see them before a publish rather than after. A version can be taken down by a moderator at any time; a moderator can also block an author, which revokes every key on the account.',
      ],
    },
    {
      heading: 'The MCP server',
      link: { to: '/connect', text: 'Set up your AI to build machines, step by step' },
      paragraphs: [
        'Download the file from the account page — one file, no dependencies, Node 22 or newer; the page shows its SHA-256 beside the link. The server speaks MCP over stdio and reads one environment variable, HCC_PINBALL_API_KEY. Without it every local tool still works and the remote tools are not offered.',
        'Claude Desktop reads claude_desktop_config.json; Cursor reads .cursor/mcp.json; VS Code reads .vscode/mcp.json with the same object under "servers" rather than "mcpServers".',
      ],
      code: [
        'claude mcp add hcc-pinball -e HCC_PINBALL_API_KEY=hpk_… -- node C:\\Tools\\hcc-pinball-mcp.js',
        '{\n  "mcpServers": {\n    "hcc-pinball": {\n      "command": "node",\n      "args": ["C:\\\\Tools\\\\hcc-pinball-mcp.js"],\n      "env": { "HCC_PINBALL_API_KEY": "hpk_…" }\n    }\n  }\n}',
      ],
      bullets: [
        'Local, no key: new_machine, validate_machine, soak_machine, shot_harness, test_rules, rule_coverage, describe_rules, screen_text, canonical_hash, export_json.',
        'With a key: builder_link, list_my_machines, get_machine, create_machine, save_version, remix_machine, validate_remote, and publish_version — irreversible, only when you have said so in so many words.',
        'Playing a draft: open its builder_link in a desktop browser, where test play runs the real simulation. The native Quest application cannot open a draft yet; publish, and the machine is in the arcade on every surface.',
      ],
    },
    {
      heading: 'Terms',
      paragraphs: [
        'A machine designed with an AI or through the Builder API is your machine, published under your name, subject to the same content rules and the same review, and you are responsible for it. The MCP server file is licensed for use with an HCC Pinball Arcade account: no redistribution, no reverse engineering. The full terms are on the terms page.',
      ],
    },
    {
      heading: 'Support',
      paragraphs: [
        `Write to Holmes Computer Consultants at ${CONTACT} and quote the requestId from any refusal; it is the join between what you saw and what the server recorded.`,
      ],
    },
  ],
}


/**
 * `/connect` (M12-29): how a subscriber points their own AI at their account.
 *
 * **The page is the short version and `ai_docs/instructions/connecting-your-ai-to-hcc-pinball.md`
 * is the long one**, and `test/docs/mcp-instructions.test.ts` holds every heading here to a
 * heading there, so the two cannot say different things about the same step. The full document is
 * also served as Markdown at `/docs/connect.md`, which is what an AI is pointed at.
 */
const CONNECT: PageCopy = {
  // **The title and the heading carry the words somebody types** (M12-31). They read "Connect
  // your AI", which is what the page is called in the navigation and is not what anybody searches
  // for: the distinctive term is **MCP** and the intent is "build a pinball machine with AI", and
  // neither appeared in the title, the description or the heading. The navigation label stays
  // short — `route.title.connect` is its own string, and a top bar is not a search result.
  title: `Build a pinball machine with your AI (MCP) — ${SITE_NAME}`,
  description:
    'Set up the HCC Pinball MCP server: point Claude, Cursor, VS Code or OpenAI Codex at your account and let your own AI design, test and save a pinball machine.',
  h1: 'Build a machine with your AI',
  intro:
    'Your own AI can design a complete machine over MCP, check its work against the same simulation the game runs, and save it to your account.',
  sections: [
    {
      heading: 'What this is',
      paragraphs: [
        'A machine in HCC Pinball Arcade is one JSON document — a playfield, the devices on it, and rules over the events they raise. It is data, never code, and that is what makes it safe to let an AI write one.',
        'These steps give the AI you already use a set of pinball tools: start from a working template, validate against the same checks the server runs, play ten thousand headless balls to find anywhere the ball gets stuck, read the rules back in plain English, and save the result to your account.',
        'You are not hosting anything. MCP calls the small program below a “server”, but it is a helper your AI starts on your own computer and talks to through its input and output. It opens no port, has no address, and is reachable by nobody. Your machines stay on this site exactly as they do when you use the Builder in a browser.',
        'There is no address to paste into ChatGPT or into Claude on the web or a phone. Those reach servers over the internet and never start a program on your machine. The five below do.',
      ],
      bullets: [
        'Node.js 22 or newer.',
        'Any signed-in account, to download the tools and use the offline ones.',
        'Pro, to make a key — which is what lets the tools save to your account.',
        'Claude Code, Claude Desktop, Cursor, VS Code or OpenAI Codex.',
      ],
    },
    {
      heading: 'Step 1 — Download the tools',
      paragraphs: [
        'Sign in, open your account page, and find “Builder API and AI tools”. Press “Get the download link”, then save the file. It is one JavaScript file with no dependencies and no installer. The link is signed and lasts ten minutes; press the button again for a fresh one.',
      ],
    },
    {
      heading: 'Step 2 — Check the file is the file',
      paragraphs: [
        'Worth thirty seconds, because this is a program you are about to let your AI run. Compare the result with the digest printed beside the download link. If they differ, delete the file and download it again.',
      ],
      code: [
        'Windows:  Get-FileHash .\\hcc-pinball-mcp-0.1.0.js -Algorithm SHA256\nmacOS:    shasum -a 256 hcc-pinball-mcp-0.1.0.js\nLinux:    sha256sum hcc-pinball-mcp-0.1.0.js',
      ],
    },
    {
      heading: 'Step 3 — Put it somewhere permanent',
      paragraphs: [
        'Your AI will run this file by its full path, so it needs to live somewhere it will not be tidied away. A downloads folder is the wrong place. Windows: C:\\Tools\\. macOS or Linux: ~/tools/.',
        'Run it once to check. It should print one line about not finding a key and then wait. That is correct: it is waiting to be spoken to over its input and output, not listening on the network. Press Ctrl+C.',
      ],
    },
    {
      heading: 'Step 4 — Make a key',
      paragraphs: [
        'On the account page, name the key after the thing that will hold it, choose what it may do, and press “Make a key”. The key is shown once. Copy it immediately; afterwards the page shows only its first eight characters, which is why a leaked list of your keys is not a list of working credentials.',
        'Leave “Publish to the arcade” unticked at first. Publishing is the irreversible one: a published version is frozen for ever and gets its own leaderboard. You can make a second key later.',
      ],
      bullets: [
        'Read my machines — list your machines and read any version of your own.',
        'Create and save drafts — make machines, save drafts, remix, run the server-side check.',
        'Publish to the arcade — put a version where other people play it.',
      ],
    },
    {
      heading: 'Step 5 — Connect your AI',
      paragraphs: [
        'Replace the path and the key with your own. On Windows, every backslash inside a JSON file must be doubled, or use forward slashes; that single detail is the most common reason a setup silently does nothing.',
        'VS Code keys its object “servers” where Claude Desktop and Cursor use “mcpServers”, and Codex needs the underscore in mcp_servers. Copying one host’s file into another without changing that key is the second most common reason.',
      ],
      code: [
        'Claude Code:\nclaude mcp add hcc-pinball -e HCC_PINBALL_API_KEY=hpk_your_key -- node C:/Tools/hcc-pinball-mcp-0.1.0.js',
        'Claude Desktop (claude_desktop_config.json) and Cursor (.cursor/mcp.json):\n{\n  "mcpServers": {\n    "hcc-pinball": {\n      "command": "node",\n      "args": ["C:/Tools/hcc-pinball-mcp-0.1.0.js"],\n      "env": { "HCC_PINBALL_API_KEY": "hpk_your_key" }\n    }\n  }\n}',
        'VS Code (.vscode/mcp.json):\n{\n  "servers": {\n    "hcc-pinball": {\n      "type": "stdio",\n      "command": "node",\n      "args": ["C:/Tools/hcc-pinball-mcp-0.1.0.js"],\n      "env": { "HCC_PINBALL_API_KEY": "hpk_your_key" }\n    }\n  }\n}',
        'OpenAI Codex:\ncodex mcp add hcc-pinball --env HCC_PINBALL_API_KEY=hpk_your_key -- node C:/Tools/hcc-pinball-mcp-0.1.0.js',
      ],
    },
    {
      heading: 'Step 6 — Check it worked',
      paragraphs: [
        'Ask your AI what pinball tools it has. There are sixteen. Eight work offline with no key: a template, the validator, the ten-thousand-ball soak, the shot harness, the rulesheet, the content screen, the document hash, and an export to a file the Builder can open. Eight more talk to your account: listing, reading, creating, saving, remixing, the server-side check, publishing, and a link back into the Builder.',
        'If only the Builder link appears from the second group, the program started without your key. Check the spelling of HCC_PINBALL_API_KEY and restart the application completely.',
      ],
    },
    {
      heading: 'Building a machine',
      paragraphs: [
        'Now just ask. A first conversation that works: “Build me a 1990s dot-matrix machine about deep-sea salvage. Three balls, a multiball, and one wizard mode. Read the format guide first. Don’t publish it.”',
        'A good AI reads the format, starts from a template, edits and validates in a loop, reads its own rulesheet to catch a mode nothing can start, soaks the machine until nothing gets stuck, tunes the shots by their make rate, and saves a private draft. Then it hands you a link and you test play it in your browser.',
        'Publishing is not something to leave to an AI. It needs the publish scope and an explicit confirmation, and your first published machine is read by a person before it appears in the arcade.',
      ],
    },
    {
      heading: 'Without a key, or without Pro',
      paragraphs: [
        'The offline half needs nothing at all. Your AI can design a complete machine, validate it, soak it and tune it, then write it to a file. Open the Builder, press “Open a file”, and choose it. Designing and test playing are free for everybody, with or without an account.',
        'What Pro buys is the machine surviving: saved to your account, opened on another device, and published so other people can play it.',
      ],
    },
    {
      heading: 'Troubleshooting',
      bullets: [
        'Nothing listed at all — the config was not read. Restart the application fully and check the file parses.',
        'Only the Builder link from the account tools — it started without a key.',
        '“That key is not one this account gave out” — wrong, truncated or mistyped key.',
        '“That key was revoked on the account page” — make a new one.',
        '“That key was not made with the machines:publish scope” — working as intended; make a key with publish ticked if you mean it.',
        '“Building machines from your own tools and AI is part of Pro” — the account is not Pro.',
        'A SyntaxError on startup — Node is older than 22.',
        'Every refusal carries a request id. Quote it if you write to support.',
      ],
    },
    {
      heading: 'Keys, and looking after them',
      paragraphs: [
        'A key lives in your AI’s configuration file in plain text, so treat that file the way you would treat a password. The program never writes your key anywhere and removes it from anything it prints.',
        'Revoke a key from the account page and anything using it stops on its next request. Revoking is instant and safe; if you are unsure about a key, revoke it. You may hold five live keys at once and each expires a year after it is made.',
        'A key reaches the Builder and nothing else. It cannot touch your subscription, your account, your headsets, or anybody else’s machines.',
      ],
    },
    {
      heading: 'What leaves your computer',
      paragraphs: [
        'The offline tools send nothing. The simulation, the validator, the soak and the shot harness all run on your machine.',
        'The account tools send the machine document you are saving, and the identification block every client of this product sends: which kind of client, its version, and a request id. Nothing else.',
      ],
    },
    {
      heading: 'Handing this to your AI instead of reading it',
      paragraphs: [
        'All of this is published where an assistant can fetch it, so you can skip the reading and say: “Read https://pinballgame.ca/docs/connect.md and set yourself up to build me a pinball machine.”',
        'An assistant that can start a program on your computer can do the rest of the setup itself from that one link. One that cannot, which means anything running in a browser or on a phone, will read the same document and tell you so.',
      ],
      bullets: [
        'https://pinballgame.ca/docs/connect.md — this guide as plain Markdown, for a machine.',
        'https://pinballgame.ca/docs/developers.md — the API reference, the same way.',
        'https://pinballgame.ca/llms.txt — what this site offers and where to read it.',
      ],
      link: { to: '/developers', text: 'The Builder API and MCP reference' },
    },
  ],
  faq: [
    {
      question: 'What is the HCC Pinball MCP server?',
      answer:
        'One JavaScript file you download from your account page and your AI runs on your own computer. MCP — the Model Context Protocol — is how an assistant is given tools, and this one gives it twenty-four pinball tools: start from a template, pick parts off a palette and place them, draw a picture of the playfield to show you, validate, soak ten thousand balls through it, measure a shot, read the rules back in English, and save, remix or publish to your account.',
    },
    {
      question: 'Do I have to run a server?',
      answer:
        'No, despite the name. Your AI starts the program, talks to it through its input and output, and stops it again. It opens no port, has no address, and nothing on the internet can reach it. There is no hosting, no account on anything else, and nothing left running when you close your assistant.',
    },
    {
      question: 'Do I need Pro to use it?',
      answer:
        'Any signed-in account can download the tools and use the offline half of them — template, validate, soak, shot harness, rulesheet, and writing the machine to a file you can open in the Builder. Pro is what lets you make a key, and a key is what lets the tools read and write machines on your account.',
    },
    {
      question: 'Is a machine my AI builds any different from one I build by hand?',
      answer:
        'No. It is the same JSON document, checked by the same validator, held to the same budgets, screened by the same moderation, and played by the same simulation. A machine is data rather than code here, so there is nothing an AI can put in one that the game would execute — which is the reason this is offered at all.',
    },
  ],
}

export const PAGE_COPY: Readonly<Record<string, PageCopy>> = {
  ...APPLICATION,
  '/privacy': PRIVACY,
  '/accessibility': ACCESSIBILITY,
  '/terms': TERMS,
  '/developers': DEVELOPERS,
  '/connect': CONNECT,
  '/support': SUPPORT,
  '/how-to-play': HOW_TO_PLAY,
  '/about': ABOUT,
}

/** The copy for a path, matching a parameterised route by its literal prefix. */
export function copyFor(pathname: string): PageCopy | undefined {
  const exact = PAGE_COPY[pathname]
  if (exact !== undefined) return exact
  if (pathname.startsWith('/play/')) return PAGE_COPY['/play/:machineId']
  if (pathname.startsWith('/machines/')) return PAGE_COPY['/machines/:machineId']
  return undefined
}
